import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertTriangle, ChevronDown, FilePenLine, Loader2, LockKeyhole, RefreshCw, Sparkles, Upload,
} from 'lucide-react';
import BatchIngestion from './BatchIngestion';
import KnowledgeIntake from './KnowledgeIntake';

const MODE_COPY = {
  ask: { short: 'Choose mode', label: 'Ask me each time' },
  standard: { short: 'Standard', label: 'Standard upload' },
  auto: { short: 'Auto', label: 'Auto-classify' },
};

const KnowledgeUploadLauncher = ({
  user,
  teams = [],
  categories = [],
  onCategoriesChange,
  initialTeamId = '',
  requireTeam = false,
  onIngestionComplete,
  triggerClassName = 'bg-gray-900 hover:bg-gray-800 text-white',
  triggerTestId = 'data-source-upload-btn',
}) => {
  const [feature, setFeature] = useState(undefined);
  const [statusError, setStatusError] = useState('');
  const [standardOpen, setStandardOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [choice, setChoice] = useState('auto');
  const [remember, setRemember] = useState(true);
  const [savingMode, setSavingMode] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusError('');
    setFeature(undefined);
    try {
      const response = await axiosInstance.get('/knowledge/intake/status');
      setFeature(response.data);
      return response.data;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 404 && detail === 'Knowledge intake is not enabled') {
        setFeature(null);
        return null;
      }
      setStatusError(typeof detail === 'string' ? detail : 'Upload options could not be loaded.');
      setFeature(null);
      return null;
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const mode = feature?.upload_mode || {};
  const effectiveMode = mode.effective_mode || mode.knowledge_upload_mode || 'ask';
  const pendingCount = feature?.unresolved_decisions || 0;

  const launch = useCallback((nextMode) => {
    setChoiceOpen(false);
    if (nextMode === 'auto') setAutoOpen(true);
    else setStandardOpen(true);
  }, []);

  const saveMode = useCallback(async (nextMode) => {
    setSavingMode(true);
    try {
      const response = await axiosInstance.put('/knowledge/intake/upload-mode', {
        knowledge_upload_mode: nextMode,
      });
      setFeature(previous => previous ? { ...previous, upload_mode: response.data } : previous);
      return response.data;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Your upload preference could not be saved.');
      return null;
    } finally {
      setSavingMode(false);
    }
  }, []);

  const handlePrimary = () => {
    if (feature?.active_batch_id) {
      setAutoOpen(true);
      return;
    }
    if (mode.locked) {
      launch(mode.locked_mode);
      return;
    }
    if (effectiveMode === 'ask') {
      setChoice('auto');
      setRemember(true);
      setChoiceOpen(true);
      return;
    }
    launch(effectiveMode);
  };

  const continueChoice = async () => {
    if (remember) {
      const saved = await saveMode(choice);
      if (!saved) return;
    }
    launch(choice);
  };

  const changeMode = async (nextMode) => {
    if (nextMode === effectiveMode && nextMode !== 'ask') return;
    const saved = await saveMode(nextMode);
    if (saved) toast.success(`${MODE_COPY[nextMode].label} saved for future uploads`);
  };

  const handleComplete = useCallback(() => {
    loadStatus();
    onIngestionComplete?.();
  }, [loadStatus, onIngestionComplete]);

  const standardProps = useMemo(() => ({
    user,
    teams,
    categories,
    onCategoriesChange,
    initialTeamId,
    requireTeam,
    onIngestionComplete: handleComplete,
  }), [user, teams, categories, onCategoriesChange, initialTeamId, requireTeam, handleComplete]);

  if (feature === undefined) {
    return (
      <Button size="sm" disabled className={triggerClassName} data-testid={`${triggerTestId}-loading`}>
        <Loader2 size={14} className="mr-1.5 animate-spin" /> Loading upload options
      </Button>
    );
  }

  if (statusError) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="knowledge-upload-status-error" role="alert">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900">
          <AlertTriangle size={14} /> Upload options unavailable
        </span>
        <span className="text-xs text-amber-800">{statusError}</span>
        <Button type="button" size="sm" variant="outline" onClick={loadStatus} className="h-7 border-amber-300 bg-white text-amber-900">
          <RefreshCw size={13} /> Retry
        </Button>
      </div>
    );
  }

  // An intentional feature-off 404 retains the established uploader exactly.
  if (!feature) {
    return (
      <BatchIngestion
        {...standardProps}
        triggerLabel="Upload files or folder"
        triggerClassName={triggerClassName}
        triggerTestId={triggerTestId}
      />
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="knowledge-upload-launcher">
        <div className="relative inline-flex">
          <Button
            size="sm"
            className={triggerClassName}
            onClick={handlePrimary}
            data-testid={triggerTestId}
          >
            <Upload size={14} className="mr-1.5" />
            {feature.active_batch_id ? 'Continue upload review' : 'Upload knowledge'}
          </Button>
          {pendingCount > 0 && (
            <button
              type="button"
              onClick={() => setAutoOpen(true)}
              className="absolute -right-2 -top-2 min-w-5 rounded-full bg-amber-500 px-1.5 py-0.5 text-center text-[10px] font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              aria-label={`Review ${pendingCount} pending upload decision${pendingCount === 1 ? '' : 's'}`}
            >
              {pendingCount}
            </button>
          )}
        </div>

        {!mode.locked && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 border-slate-300 bg-white px-2 text-xs text-slate-700"
                aria-label="Change upload mode"
                disabled={savingMode}
                data-testid="knowledge-upload-mode-switch"
              >
                {effectiveMode === 'auto' && <Sparkles size={13} className="text-violet-600" />}
                {effectiveMode === 'standard' && <FilePenLine size={13} />}
                {MODE_COPY[effectiveMode]?.short || 'Choose mode'} <ChevronDown size={13} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Default upload mode</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={effectiveMode} onValueChange={changeMode}>
                <DropdownMenuRadioItem value="auto">Auto-classify</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="standard">Standard upload</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="ask">Ask me each time</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {mode.locked && (
          <span className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs text-slate-600" title="Set by your organisation administrator">
            <LockKeyhole size={12} /> {MODE_COPY[mode.locked_mode]?.label}
          </span>
        )}

      </div>

      <Dialog open={choiceOpen} onOpenChange={setChoiceOpen}>
        <DialogContent className="max-w-xl" data-testid="knowledge-upload-mode-prompt">
          <DialogHeader>
            <DialogTitle>Choose upload mode</DialogTitle>
            <p className="text-sm text-slate-500">Upload files or folders using either workflow.</p>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setChoice('standard')}
              className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${choice === 'standard' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:bg-slate-50'}`}
              aria-pressed={choice === 'standard'}
            >
              <FilePenLine size={19} className="shrink-0 text-slate-600" />
              <span className="min-w-0"><span className="block font-semibold text-slate-900">Standard upload</span><span className="block text-xs text-slate-600">Enter document details yourself.</span></span>
            </button>
            <button
              type="button"
              onClick={() => setChoice('auto')}
              className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${choice === 'auto' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}
              aria-pressed={choice === 'auto'}
            >
              <Sparkles size={19} className="shrink-0 text-violet-600" />
              <span className="min-w-0 flex-1"><span className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">Auto-classify <span className="rounded-full bg-violet-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800">Recommended</span></span><span className="block text-xs text-slate-600">Review Qflow’s suggestions before publishing.</span></span>
            </button>
          </div>
          <div className="flex flex-col gap-3 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={remember} onChange={event => setRemember(event.target.checked)} className="h-4 w-4 rounded border-slate-300" />
              Remember my choice
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setChoiceOpen(false)}>Cancel</Button>
              <Button onClick={continueChoice} disabled={savingMode}>
                {savingMode && <Loader2 size={15} className="animate-spin" />} Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BatchIngestion {...standardProps} open={standardOpen} onOpenChange={setStandardOpen} hideTrigger />
      <KnowledgeIntake user={user} onPublished={handleComplete} open={autoOpen} onOpenChange={setAutoOpen} hideTrigger />
    </>
  );
};

export default KnowledgeUploadLauncher;
